import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';
import { TextEditorState } from '../textEditor/textEditorState';
import { input, inputGroup, InputGroupState, InputState } from '../input';
import { notifyError, notifyLoading, notifySuccess, notifyWarning } from '../../services/notifications';
import { JobModel } from '../../entities';
import { BlockDiff } from '../textEditor/suggestions/blockDiffTypes';
import { ValueAtRiskState, ValueAtRiskSuggestion } from './valueAtRisk/valueAtRiskState';

// public_safety.py abandons a revision by clearing publicSafetyReportRevising and stamping
// one of these. The poll below cannot tell that apart from success on the flag alone, so a
// code missing from this set reloads the unchanged report and announces it as revised.
const REVISION_EDITED = 'PublicSafetyReportRevisionEdited';
const REVISION_FAILURE_ERRORS = new Set([
  'PublicSafetyReportRevisionSourceMissing',
  'PublicSafetyReportRevisionIncomplete',
  REVISION_EDITED,
]);

function revisionFailureMessage(ingestError: unknown): string {
  return ingestError === REVISION_EDITED
    ? 'The notes or report changed while they were being regenerated, so nothing was replaced. Try again.'
    : 'Report revision failed. Try again.';
}

export type EditorWindowMode = 'notes' | 'report' | 'translation' | 'dualPlusReport' | 'customerFollowUpLetter';

// The backend refuses an edit the caller lacks permission for by throwing `Unauthorized`, which
// runGraphQlApiRequest turns into a NotAuthorized ApiError. Reporting that as "try again later"
// sent a Benton County deputy chasing his network for a week and a half while every save was
// being rejected on his team role -- retrying can never clear it, so say so.
//
// `type` alone is not enough: ApiService also returns NotAuthorized when it cannot build an auth
// context at all (an expired session, before any request is sent). Telling THAT user to go ask
// their library owner sends them down the same wrong path in the opposite direction -- their
// retry-after-signing-in genuinely would have worked. ApiError only carries a GraphQL response
// when the inner error was a ClientError, i.e. the backend answered and refused, so require it.
const NO_EDIT_PERMISSION_MESSAGE = 'You do not have permission to edit this report. Ask your library owner for edit access.';

function reportErrorMessage(err: unknown, fallback: string): string {
  const apiError = err as { type?: string; originalErrorResponse?: unknown } | null;
  return apiError?.type === 'NotAuthorized' && !!apiError.originalErrorResponse
    ? NO_EDIT_PERMISSION_MESSAGE
    : fallback;
}

// The three refusals reviseReport is written to answer with. Everything else it can throw is
// machinery -- DynamoDB, S3, Elasticsearch, Lambda -- whose message means nothing to a deputy, so
// this is an exact allowlist rather than a pass-through of whatever GraphQL returned. "Try again
// later" is wrong for all three: a retry alone clears none of them.
const REVISION_IN_PROGRESS = 'A revision is already in progress for this report.';
const EDITED_DOCUMENTS = 'The notes or report were edited, changed by feedback, or certified, so they were not re-run.';
const SPEAKER_NAMES_FEEDBACK = 'Use the speaker names set on the swimlanes.';

// Jobs with no notes to name speakers in; a background apply skips them without a word.
const SPEAKER_NAMES_NOT_APPLICABLE = [
  'Cannot revise: job has no report generated yet.',
  'This report has no notes to re-run.',
  'Combined reports cannot be revised yet.',
];
const REVISE_REFUSALS = [
  REVISION_IN_PROGRESS,
  EDITED_DOCUMENTS,
  'Feedback must be at least 5 characters.',
  'Untick Certify to re-run the notes and report.',
  ...SPEAKER_NAMES_NOT_APPLICABLE,
];
const SPEAKER_NAMES_PENDING_KEY = 'speakerNamesPending';

// Matched with `includes` rather than equality only because AppSync may prefix the thrown message;
// the allowlisted sentence is still what gets shown, never the surrounding text.
function reviseErrorMessage(err: unknown, fallback: string): string {
  const graphQlErrors = (err as { originalErrorResponse?: { errors?: { message?: string }[] } } | null)
    ?.originalErrorResponse?.errors ?? [];
  const refusal = REVISE_REFUSALS.find(
    known => graphQlErrors.some(({ message }) => !!message && message.includes(known)),
  );
  return refusal ?? reportErrorMessage(err, fallback);
}

// The server's validation messages name the field ("Buildings row 2 rate must be…"), so show the
// first one; anything else gets the permission-aware report message.
function valueAtRiskErrorMessage(err: unknown): string {
  const message = (err as { originalErrorResponse?: { errors?: { message?: string }[] } } | null)
    ?.originalErrorResponse?.errors?.[0]?.message;
  if (message && message !== 'Unauthorized') return message;
  return reportErrorMessage(err, 'Could not save Value at Risk. Try again later.');
}

type SuggestionCategory = {
  label: string;
  blockIndices: number[];
};

export class EditorWindowState
  extends StoreNode {

  readonly nodeType = 'EditorWindow';
  readonly publicSafetyReportComplete: InputState;
  readonly publicSafetyReportFeedback: InputState;
  readonly reviseEnabled: InputState;
  readonly submitButton: InputState;
  readonly inputGroup: InputGroupState;

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'reloadReportContent' | 'reset'>(this, {
      isLoading: observable,
      data: observable,
      fileName: observable,
      jobId: observable,
      aiDraftContent: observable,
      isManualReport: observable,
      isReadOnly: observable,
      isRevising: observable,
      isValueAtRiskOpen: observable,
      isSavingValueAtRisk: observable,
      isPrefillingValueAtRisk: observable,
      creatingLetterJobId: observable,
      revisingJobIds: observable,
      isCreatingLetter: computed,
      mode: observable,
      editorMode: observable,
      suggestionDetail: observable,
      maxDetail: observable,
      categories: observable,
      reportDiff: observable,
      isDiffLoading: observable,
      isDiffAvailable: observable,
      originalContent: observable,
      job: computed,
      canEditValueAtRisk: computed,
      isValueAtRiskLocked: computed,
      toggleValueAtRisk: action,
      canReviseReport: computed,
      contentMatchesAiDraft: computed,
      open: action,
      onSave: action,
      revise: action,
      applySpeakerNames: action,
      reloadReportContent: action,
      setSuggestionDetail: action,
      acceptAllSuggestions: action,
      hideDiff: action,
      fetchReportDiff: action,
      close: action,
      reset: action,
    });

    this.window.listen(
      this.windowListener);
    reaction(() => this.store.user?.id, () => this.resumeSpeakerNames(), { fireImmediately: true });

      this.publicSafetyReportComplete = input(this, {
        name: 'publicSafetyReportComplete',
        isRequired: () => {
          // Certification finalizes a review, but NOT when requesting a revise or clearing a certification.
          return this.mode === 'report'
            && !this.job?.publicSafetyReportComplete
            && !(this.canReviseReport && this.reviseEnabled.value);
        }
      });

      this.reviseEnabled = input(this, {
        name: 'reviseEnabled',
        label: 'Re-run this report incorporating this feedback',
      });

      this.publicSafetyReportFeedback = input(this, {
        name: 'publicSafetyReportFeedback',
        label: 'How can we make your next first draft better?',
        placeholder: 'Share ideas, preferences, or examples…',
        error: (input, fallback) =>
          (input.value && input.value.length < 5) ? 'Add at least 5 characters' : fallback,
        showStatusMessage: true,
      });
  
      this.submitButton = input(this, {
        name: 'saveButton',
        disabled: (input: InputState) => {
          return (
            !!input.loading ||
            input.isSubmitting ||
            this.inputGroup.hasTouchedErrorInputs ||
            !this.inputGroup.hasChangedInputs);
        }
      });
  
      this.inputGroup = inputGroup(this, {
        name: 'draftReview',
        inputs: [
          this.publicSafetyReportComplete,
          this.publicSafetyReportFeedback,
          this.reviseEnabled,
          this.submitButton
        ],
        isSubmitDisabled: (form) => {
          return form.hasErrorInputs || !form.hasChangedInputs;
        }
      });
  }

  readonly window = new WindowState(this.store, { closeOnOutsideClick: false });
  readonly editor = new TextEditorState(this.store);
  readonly valueAtRisk = new ValueAtRiskState();
  
  isLoading = false;
  data: string | null = null;
  fileName: string | null = null;
  jobId: string | null = null;
  aiDraftContent: string | null = null;
  isManualReport: boolean = false;
  isReadOnly: boolean = false;
  isRevising = false;
  isValueAtRiskOpen = false;
  isSavingValueAtRisk = false;
  isPrefillingValueAtRisk = false;

  // This is a stupid workaround and has to be removed
  mode: EditorWindowMode | null = null;
  editorMode: 'suggestions' | 'collaboration' = 'collaboration';

  suggestionDetail: number | null = null;
  maxDetail: number = 1;
  categories: SuggestionCategory[] = [];
  reportDiff: BlockDiff[] | null = null;
  isDiffLoading: boolean = false;
  isDiffAvailable: boolean | null = null;
  originalContent: string | null = null;

  /** The job whose customer follow-up letter is being created after certify; survives closing the editor. */
  creatingLetterJobId: string | null = null;

  /** The job whose notes or report are being re-run; survives closing the editor. */
  revisingJobIds: string[] = [];

  /** Covers the certified report while its letter is created; the letter then opens in its place. */
  get isCreatingLetter(): boolean {
    return !!this.jobId && this.creatingLetterJobId === this.jobId && this.mode === 'report';
  }

  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  /**
   * Value at Risk is offered on an editable report of a job the server says qualifies (Loss
   * Adjuster, or an Insurance org).
   */
  private get offersValueAtRisk(): boolean {
    return this.mode === 'report'
      && !this.isReadOnly
      && !this.isManualReport
      && !!this.job?.valueAtRiskAvailable
      && !!this.job.hasPermission('UserEditJob');
  }

  get canEditValueAtRisk(): boolean {
    return this.offersValueAtRisk && !this.job?.publicSafetyReportComplete;
  }

  /** A certified report is the approved version, so its figures wait for the certification to be cleared. */
  get isValueAtRiskLocked(): boolean {
    return this.offersValueAtRisk && !!this.job?.publicSafetyReportComplete;
  }

  /** Only Save to report puts the form in the report, so anything else asks before dropping it. */
  private confirmDropValueAtRisk(): boolean {
    if (!this.valueAtRisk.isDirty) return true;
    if (!window.confirm('Your Value at Risk changes are not saved, so they are not in the report. Continue without them?'))
      return false;
    this.valueAtRisk.load(this.job?.valueAtRisk);
    return true;
  }

  private warnBeforeUnload = (event: BeforeUnloadEvent) => {
    if (this.valueAtRisk.isDirty) event.preventDefault();
  }

  toggleValueAtRisk = () => {
    this.isValueAtRiskOpen = !this.isValueAtRiskOpen;
  }

  /**
   * Saves the editor first so the server splices the block into the text the adjuster sees, then
   * reloads the report so a later autosave can't write back a copy without the block.
   */
  saveValueAtRisk = async (): Promise<boolean> => {
    if (!this.jobId || this.isSavingValueAtRisk) return false;
    runInAction(() => { this.isSavingValueAtRisk = true; });
    try {
      if (!await this.editor.saveNow()) {
        notifyError(this, 'Could not save the report. Value at Risk was not saved.');
        return false;
      }
      const inputJson = JSON.stringify(this.valueAtRisk.input);
      const [, err] = await this.store.apiUpdateJob({ id: this.jobId, valueAtRisk: inputJson });
      if (err) {
        notifyError(this, valueAtRiskErrorMessage(err));
        return false;
      }
      this.valueAtRisk.markSaved(inputJson);
      const [, fetchErr] = await this.store.jobManager.apiFetchJob(this.jobId);
      if (fetchErr) {
        notifyError(this, 'Value at Risk was saved. Reopen the report to see it.');
        return false;
      }
      this.reloadReportContent();
      notifySuccess(this, 'Value at Risk saved to the report.');
      return true;
    } finally {
      runInAction(() => { this.isSavingValueAtRisk = false; });
    }
  }

  /** Asks the server for rows heard in the recording and merges them into the form, unsaved. */
  prefillValueAtRisk = async (): Promise<void> => {
    if (!this.jobId || this.isPrefillingValueAtRisk) return;
    runInAction(() => { this.isPrefillingValueAtRisk = true; });
    try {
      const [res, err] = await this.store.apiService.getJobValueAtRiskSuggestion({ id: this.jobId });
      if (err) {
        notifyError(this, valueAtRiskErrorMessage(err));
        return;
      }
      const raw: unknown = res?.getJob?.valueAtRiskSuggestion;
      const suggestion = (typeof raw === 'string' ? JSON.parse(raw) : raw) as ValueAtRiskSuggestion | null;
      const filled = suggestion ? this.valueAtRisk.applySuggestion(suggestion) : 0;
      if (filled) {
        notifySuccess(this, `Pre-filled ${filled} row${filled === 1 ? '' : 's'} from the recording. Check them, add the rates, then save.`);
      } else {
        notifySuccess(this, 'No measurements or amounts were found in the recording.');
      }
    } finally {
      runInAction(() => { this.isPrefillingValueAtRisk = false; });
    }
  }

  get canReviseReport(): boolean {
    return (this.mode === 'report' || this.mode === 'notes') && !this.isManualReport;
  }

  get contentMatchesAiDraft(): boolean {
    if (!this.originalContent || !this.aiDraftContent) return false;
    const toText = (html: string) => {
      // Replace <br> with spaces (textContent skips self-closing elements)
      // and add spaces after block-closing tags so textContent doesn't
      // concatenate text across boundaries without separation.
      const spaced = html
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/(p|h[1-6]|li|div|blockquote|tr|td|th|ul|ol)>/gi, '</$1> ');
      const el = document.createElement('div');
      el.innerHTML = spaced;
      return (el.textContent ?? '').replace(/[\s\u00a0]+/g, ' ').trim();
    };
    return toText(this.originalContent) === toText(this.aiDraftContent);
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        if (this.isLoading || !this.confirmDropValueAtRisk())
          return;
        this.close();
        break;
    }
  }

  open({ jobId, data, fileName, mode, aiDraftContent, editorMode, isManualReport, readOnly }: { jobId?: string, data?: string, fileName?: string, mode?: EditorWindowMode, aiDraftContent?: string, editorMode?: 'suggestions' | 'collaboration', isManualReport?: boolean, readOnly?: boolean }) {
    this.jobId = jobId ?? null;
    this.editor.setJobId(jobId ?? null);
    this.editor.setBranding(null);
    // Best effort and unawaited: the editor opens on the plain surface and repaints if the job's
    // custom report format turns out to carry the customer's page furniture.
    void this.editor.loadBranding(this.job?.id);
    this.data = data ?? null;
    this.fileName = fileName ?? null;
    this.mode = mode ?? null;
    this.aiDraftContent = aiDraftContent ?? null;
    this.isManualReport = isManualReport ?? false;
    // A job reached through an agency ACL is viewable without being editable. The backend
    // refuses the save and the certification, so don't offer either.
    this.isReadOnly = (readOnly ?? false) || (!!this.job && !this.job.hasPermission('UserEditJob'));
    this.editorMode = editorMode ?? 'collaboration';
    this.originalContent = data ?? null;
    this.reportDiff = null;
    this.isDiffLoading = false;
    this.isDiffAvailable = null;
    this.suggestionDetail = null;
    this.categories = [];
    this.dispatch('Overlays', 'openWindow', { name: 'EditorWindow' });
    this.emit('open');
    this.window.open();

    this.publicSafetyReportComplete.loadValue(this.job?.publicSafetyReportComplete);
    this.valueAtRisk.load(this.job?.valueAtRisk);
    this.isValueAtRiskOpen = false;
    window.addEventListener('beforeunload', this.warnBeforeUnload);
  }

  onSave = async (content?: string): Promise<boolean> => {
    if (this.mode === 'translation' || this.isReadOnly) return false;

    try {
      const [, err] = await this.store.apiUpdateJob({
          id: this.jobId || '',
          ...(this.mode === 'notes'
            ? { publicSafetyNotes: content }
            : {}),
          ...(this.mode === 'report'
            ? (this.isManualReport
              ? { publicSafetyManualReport: content }
              : { publicSafetyReport: content })
            : {}),
          ...(this.mode === 'customerFollowUpLetter'
            ? { publicSafetyCustomerFollowUpLetter: content }
            : {}),
          ...(this.mode === 'dualPlusReport'
            ? { publicSafetyDualPlusReport: content }
            : {}),
      });

      if (err) {
        this.inputGroup.handleSubmitReject();
        notifyError(this, reportErrorMessage(err, 'Could not update the report. Try again later.'));
        return false;
      }

      // After saving a manual report, reset diff state so suggestions
      // are recomputed from scratch on next "Show AI Suggestions" click
      if (this.mode === 'report' && this.isManualReport && content) {
        runInAction(() => {
          this.originalContent = content;
          this.reportDiff = null;
          this.isDiffAvailable = null;
          this.suggestionDetail = null;
          this.maxDetail = 1;
          this.categories = [];
        });
      }

      return true;
    } catch (_e) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Could not update job. Try again later.');
      return false;
    }
  }

  submit = async (confirmed = false): Promise<boolean> => {
    // Guided training certifies through here without the (hidden) footer.
    if (this.isReadOnly) {
      notifyError(this, NO_EDIT_PERMISSION_MESSAGE);
      return false;
    }
    if (!confirmed && this.mode === 'notes' && this.canReviseReport && this.reviseEnabled.value
      && !window.confirm('Re-run the notes and the report? This regenerates both, and any edits you made to them will be replaced.'))
      return false;

    this.inputGroup.handleSubmit();
    if (this.inputGroup.error) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Fix the validation errors before saving.');
      return false;
    }
    if (!this.confirmDropValueAtRisk()) {
      this.inputGroup.handleSubmitReject();
      return false;
    }

    if (this.canReviseReport && this.reviseEnabled.value) {
      return await this.revise();
    }

    try {
      notifyLoading(this, `Submitting review...`);
      // Capture the certification request before awaiting it. Closing the editor resets the
      // mutable window state, but must not prevent this session discovering the resulting letter.
      const submittedJobId = this.jobId;
      const submittedReportCertification = this.mode === 'report'
        && !!this.publicSafetyReportComplete.value;
      // The letter can be written before the certify response returns, which then reports nothing
      // pending; a letter pending before the submit still has to be pulled in.
      const letterWasPending = !!this.job?.customerFollowUpLetterPending;

      const [updatedJob, err] = await this.store.apiUpdateJob({
          id: submittedJobId || '',
          publicSafetyNotesFeedback: this.mode === 'notes' ?  (this.publicSafetyReportFeedback.value || '') : undefined,
          publicSafetyReportFeedback: this.mode === 'report' ?  (this.publicSafetyReportFeedback.value || '') : undefined,
          ...(this.mode === 'report' ? {
            publicSafetyReportComplete: !!this.publicSafetyReportComplete.value,
          } : {}),
          // No report body here on purpose. submit() calls inputGroup.handleSubmit() above, which
          // cascades to every child input and clears isChangedSinceLastSubmit, so a branch gated on
          // that flag could never run -- certifying has only ever sent the flag and the feedback.
          // The body reaches S3 through onSave, which writes publicSafetyManualReport or
          // publicSafetyReport according to isManualReport. Making certify persist the body is a
          // product decision (it would start writing on a path that has never written), not a
          // mechanical fix.
      });

      if (err) {
        this.inputGroup.handleSubmitReject();
        notifyError(this, reportErrorMessage(err, 'Could not update the report state. Try again later.'));
        return false;
      }

      this.inputGroup.handleSubmitResolve();
      this.publicSafetyReportFeedback.reset();
      notifySuccess(this, `Review submitted successfully.`);

      if (
        submittedReportCertification
        && (updatedJob?.customerFollowUpLetterPending || letterWasPending)
        && submittedJobId
        && !this.isDisposed
      ) {
        void this.pollUntilLetterReady(submittedJobId);
      }

      return true;

    } catch (_e) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Could not submit your review. Try again later.');
      return false;
    }
  }

  applySpeakerNames = async (): Promise<boolean> => {
    // A confirmation modal would replace this window and unmount the editor mid-save.
    if (!window.confirm('Apply speaker names? This regenerates the notes and the report. Any edits you made to them will be replaced.\n\nNext time, label speakers before editing, or describe your changes in the feedback and re-run.'))
      return false;
    const feedback = this.publicSafetyReportFeedback.value;
    const reviseEnabled = this.reviseEnabled.value;
    const typed = (feedback || '').replaceAll(SPEAKER_NAMES_FEEDBACK, '').trim();
    this.publicSafetyReportFeedback.handleChange(null, [typed, SPEAKER_NAMES_FEEDBACK].filter(Boolean).join('\n'));
    this.reviseEnabled.loadValue(true);
    if (await this.submit(true)) return true;
    // A refused run must not leave the sentence behind to be sent as feedback on the next submit.
    this.publicSafetyReportFeedback.handleChange(null, feedback);
    this.reviseEnabled.loadValue(reviseEnabled);
    return false;
  }

  revise = async (): Promise<boolean> => {
    const feedback = (this.publicSafetyReportFeedback.value || '').trim();
    if (feedback.length < 5) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Add at least 5 characters of feedback to revise.');
      return false;
    }
    if (this.editor.hasUnresolvedSuggestions) {
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Accept or reject track changes before revising.');
      return false;
    }

    try {
      notifyLoading(this, 'Starting report revision…');
      // A save with nothing changed still counts as an edit, and would stop a background run publishing.
      const unsaved = this.editor.saveStatus !== 'idle' && this.editor.saveStatus !== 'saved';
      if (unsaved && !await this.editor.saveNow()) {
        this.inputGroup.handleSubmitReject();
        notifyError(this, 'Could not save the current report. Revision was not started.');
        return false;
      }
      const [, err] = await this.store.apiReviseReport({ jobId: this.jobId || '', feedback, notes: this.mode === 'notes' });
      if (err) {
        this.inputGroup.handleSubmitReject();
        notifyError(this, reviseErrorMessage(err, 'Could not start the revision. Try again later.'));
        return false;
      }
      // A notes re-run reads the names now, so the armed background apply would only repeat it.
      if (this.mode === 'notes') this.cancelSpeakerNames(this.jobId || '');

      runInAction(() => { this.isRevising = true; });
      this.inputGroup.handleSubmitResolve();
      notifySuccess(this, 'Revision started — generating your updated report…');

      return await this.pollUntilRevised(this.jobId || '');
    } catch (_e) {
      runInAction(() => { this.isRevising = false; });
      this.inputGroup.handleSubmitReject();
      notifyError(this, 'Could not start the revision. Try again later.');
      return false;
    }
  }

  // Default 5s; overridable to 0 in tests so the poll loop does not use a real timer.
  pollIntervalMs = 5000;

  speakerNamesDelayMs = 20_000;
  private speakerNamesTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /** Re-runs the job's notes and report with its speaker names once labelling pauses. */
  scheduleSpeakerNames(jobId: string, delayMs = this.speakerNamesDelayMs) {
    clearTimeout(this.speakerNamesTimers.get(jobId));
    this.speakerNamesTimers.set(jobId, setTimeout(() => void this.autoApplySpeakerNames(jobId), delayMs));
    this.setSpeakerNamesPending(jobId, true);
  }

  // A pending apply survives a reload or a closed tab and runs on the user's next visit. Their other tabs share the list.
  private get speakerNamesPendingKey() {
    return this.store.user ? `${SPEAKER_NAMES_PENDING_KEY}:${this.store.user.id}` : null;
  }

  private pendingSpeakerNames(): string[] {
    const key = this.speakerNamesPendingKey;
    if (!key) return [];
    try {
      const pending: unknown = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(pending) ? pending.filter((jobId): jobId is string => typeof jobId === 'string') : [];
    } catch {
      return [];
    }
  }

  private setSpeakerNamesPending(jobId: string, pending: boolean) {
    const key = this.speakerNamesPendingKey;
    if (!key) return;
    const others = this.pendingSpeakerNames().filter(id => id !== jobId);
    window.localStorage.setItem(key, JSON.stringify(pending ? [...others, jobId] : others));
  }

  private resumeSpeakerNames() {
    this.pendingSpeakerNames().forEach(jobId => this.scheduleSpeakerNames(jobId));
  }

  private cancelSpeakerNames(jobId: string) {
    clearTimeout(this.speakerNamesTimers.get(jobId));
    this.speakerNamesTimers.delete(jobId);
    this.setSpeakerNamesPending(jobId, false);
  }

  private autoApplySpeakerNames = async (jobId: string) => {
    // Tabs share the pending list but not their timers, so a job gone from it was applied elsewhere.
    const appliedElsewhere = this.speakerNamesPendingKey && !this.pendingSpeakerNames().includes(jobId);
    this.cancelSpeakerNames(jobId);
    if (appliedElsewhere) return;
    const [, err] = await this.store.apiReviseReport({ jobId, feedback: SPEAKER_NAMES_FEEDBACK, notes: true, onlyIfUnedited: true });
    if (!err) {
      await this.pollUntilRevised(jobId);
      return;
    }
    const message = reviseErrorMessage(err, '');
    if (message === REVISION_IN_PROGRESS)
      this.scheduleSpeakerNames(jobId, 60_000);
    else if (message === EDITED_DOCUMENTS)
      notifyWarning(this, 'Speaker names were not applied because the notes or report have edits or feedback changes. Apply speaker names in Notes to apply them; it replaces your edits. Next time, label speakers before editing, or describe your changes in the feedback and re-run.');
    else if (!SPEAKER_NAMES_NOT_APPLICABLE.includes(message))
      notifyError(this, 'Speaker names could not be applied. Use Apply speaker names in Notes to apply them.');
  };

  private pollUntilRevised = async (jobId: string): Promise<boolean> => {
    runInAction(() => { this.revisingJobIds.push(jobId); });
    try {
      return await this.pollRevision(jobId);
    } finally {
      runInAction(() => {
        this.revisingJobIds.splice(this.revisingJobIds.indexOf(jobId), 1);
      });
    }
  };

  private pollRevision = async (jobId: string): Promise<boolean> => {
    const MAX_POLLS = 120; // ~10 min ceiling — public-safety Lambda timeout is 900s

    for (let i = 0; i < MAX_POLLS; i++) {
      if (this.pollIntervalMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, this.pollIntervalMs));
      }

      // Narrow status probe — avoid re-pulling the full report/related jobs every tick.
      const [res, err] = await this.store.api.runQuery('getJob_checkReportRevising', { id: jobId });
      if (err) continue;
      // The worker clears publicSafetyReportRevising ONLY in the same write that persists a
      // regenerated report — a failed run leaves it set — so a cleared (falsy) flag is the
      // success edge. Still running: keep polling.
      if (res?.getJob?.publicSafetyReportRevising) continue;

      // Ready: pull the heavyweight report once and refresh the editor.
      const [job, fetchErr] = await this.store.jobManager.apiFetchJob(jobId);
      if (fetchErr) continue;
      // The run may have renamed speakers; swimlanes and transcripts read names from the speaker list.
      await job?.apiBatchFetchSpeakers();
      // One notification shows at a time, so a run's warning stands in for its success message.
      const warning = job?.publicSafetySpeakerNamesWarning;
      // Only this job's AI notes or report may be replaced; a letter or manual report open here stays as it is.
      if (this.jobId !== jobId || !this.canReviseReport) {
        const failed = REVISION_FAILURE_ERRORS.has(job?.ingestError as string);
        if (failed)
          notifyError(this, revisionFailureMessage(job?.ingestError));
        else if (warning)
          notifyWarning(this, warning);
        return !failed;
      }
      if (!this.job?.publicSafetyReport) {
        runInAction(() => { this.isRevising = false; });
        notifyError(this, 'The revision finished without producing a report. Try again later.');
        return false;
      }

      // A failed revision usually DOES still have a report — the previous one. The worker
      // clears the revising flag on its way out and stamps ingestError, so the flag alone
      // cannot tell failure from success; without this the editor reloads the unchanged
      // report and announces it as revised.
      if (REVISION_FAILURE_ERRORS.has(this.job.ingestError as string)) {
        runInAction(() => { this.isRevising = false; });
        this.inputGroup.handleSubmitReject();
        notifyError(this, revisionFailureMessage(this.job.ingestError));
        return false;
      }

      const submitted = this.isRevising;
      runInAction(() => { this.isRevising = false; });
      // Reloading would drop typing not yet saved, so the user's version stays and autosave keeps it.
      if (!submitted && this.editor.saveStatus !== 'idle' && this.editor.saveStatus !== 'saved') {
        notifyWarning(this, 'Speaker names were applied, but you were typing, so your version of the notes is kept without the new names. Use Apply speaker names to apply them; it replaces your edits.');
        return true;
      }
      this.reloadReportContent();
      // Feedback typed while a background apply ran is the user's, not this run's.
      if (submitted) {
        this.publicSafetyReportFeedback.reset();
        this.reviseEnabled.loadValue(false);
      }
      if (warning)
        notifyWarning(this, warning);
      else
        notifySuccess(this, 'Your revised report is ready.');
      return true;
    }

    runInAction(() => { this.isRevising = false; });
    notifyError(this, 'Revision is taking longer than expected. Refresh to check again.');
    return false;
  }

  private reloadReportContent = () => {
    const content = (this.mode === 'notes' ? this.job?.publicSafetyNotes : this.job?.publicSafetyReport) ?? null;
    this.data = content;
    this.originalContent = content;
    if (content) {
      this.editor.resetToOriginalContent(content);
    }
    // Suggestion/diff state was computed against the old report — invalidate it.
    this.reportDiff = null;
    this.isDiffAvailable = null;
    this.suggestionDetail = null;
    this.maxDetail = 1;
    this.categories = [];
  }

  setSuggestionDetail(detail: number) {
    this.suggestionDetail = Math.max(1, Math.min(this.maxDetail, detail));
  }

  acceptAllSuggestions = async () => {
    // If not at max detail, fetch full diff first so ALL suggestions
    // are loaded into the editor before accepting.
    if (this.suggestionDetail !== null && this.suggestionDetail < this.maxDetail) {
      this.suggestionDetail = this.maxDetail;
      await this.fetchReportDiff();
      // Wait for React useEffect to propagate reportDiff to TextEditorState
      await new Promise(r => setTimeout(r, 50));
    }

    // Accept all track change suggestions in the editor
    this.editor.acceptAllSuggestions();

    // Save the accepted content to the backend
    const content = this.editor.editorModel as string;
    await this.onSave(content);

    // Reset diff state — next "Show AI Suggestions" will find no changes
    runInAction(() => {
      this.originalContent = content;
      this.reportDiff = null;
      this.isDiffAvailable = false;
      this.suggestionDetail = null;
      this.maxDetail = 1;
      this.categories = [];

      // Restore clean editor state with accepted content
      this.editor.resetToOriginalContent(content);
    });
  }

  hideDiff = () => {
    this.reportDiff = null;
    this.isDiffAvailable = null;
    this.suggestionDetail = null;
    this.maxDetail = 1;
    this.categories = [];
    // Restore original content in editor
    if (this.originalContent) {
      this.editor.resetToOriginalContent(this.originalContent);
    }
  }

  fetchReportDiff = async () => {
    if (!this.jobId) return;

    this.isDiffLoading = true;

    try {
      const [res, err] = await this.store.apiService.getJobReportDiff({
        id: this.jobId,
        ...(this.suggestionDetail !== null ? { suggestionDetail: this.suggestionDetail } : {}),
      });

      if (err) {
        runInAction(() => {
          this.isDiffLoading = false;
          this.isDiffAvailable = false;
        });
        return;
      }

      const diffJson = res?.getJob?.publicSafetyReportDiff ?? null;

      runInAction(() => {
        if (diffJson === null) {
          this.isDiffAvailable = false;
          this.isDiffLoading = false;
          return;
        }

        try {
          const parsed = JSON.parse(diffJson);
          // Support both new shape { diffs, maxDetail } and legacy BlockDiff[]
          let diffs: BlockDiff[];
          if (Array.isArray(parsed)) {
            diffs = parsed as BlockDiff[];
            this.maxDetail = 1;
          } else {
            diffs = parsed.diffs as BlockDiff[];
            this.maxDetail = parsed.maxDetail ?? 1;
            this.categories = (parsed.categories ?? []) as SuggestionCategory[];
            // On first load, use backend-computed default
            if (this.suggestionDetail === null) {
              this.suggestionDetail = parsed.defaultDetail ?? 1;
            }
          }

          // No actual changes — report matches the AI draft
          const hasChanges = diffs.some(d => d.type !== 'unchanged');
          if (!hasChanges) {
            this.isDiffAvailable = false;
            this.isDiffLoading = false;
            return;
          }

          this.reportDiff = diffs;
          this.isDiffAvailable = true;
        } catch {
          this.isDiffAvailable = false;
        }

        this.isDiffLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isDiffLoading = false;
        this.isDiffAvailable = false;
      });
    }
  }

  close() {
    window.removeEventListener('beforeunload', this.warnBeforeUnload);
    this.reset();
    this.dispatch('Overlays', 'closeWindow');
    this.window.close();
    this.emit('close');
  }

  // The letter is written asynchronously after certify; poll the narrow flag so it shows as soon as it exists.
  private pollUntilLetterReady = async (jobId: string): Promise<void> => {
    const MAX_POLLS = 60; // ~5 min; a failed generation leaves the letter pending for good
    runInAction(() => { this.creatingLetterJobId = jobId; });
    try {
      for (let i = 0; i < MAX_POLLS; i++) {
        if (this.pollIntervalMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, this.pollIntervalMs));
        }
        if (this.isDisposed) return;
        const [res, err] = await this.store.api.runQuery('getJob_checkCustomerFollowUpLetterPending', { id: jobId });
        if (err || res?.getJob?.customerFollowUpLetterPending !== false) continue;
        // Pulls the letter into the job, which shows its button under the video.
        const [, fetchErr] = await this.store.jobManager.apiFetchJob(jobId);
        if (fetchErr) continue;
        notifySuccess(this, 'The customer follow-up letter is ready.');
        const job = this.job;
        const letter = job?.publicSafetyCustomerFollowUpLetter;
        if (this.isCreatingLetter && letter) {
          this.open({ jobId, data: letter, fileName: `${job.title}-follow-up-letter.pdf`, mode: 'customerFollowUpLetter' });
          // The mounted editor keeps the report it loaded; swap the content as a re-run does.
          this.editor.resetToOriginalContent(letter);
        }
        return;
      }
      notifyError(this, 'The customer follow-up letter is taking longer than expected. Refresh to check again.');
    } finally {
      runInAction(() => {
        if (this.creatingLetterJobId === jobId) this.creatingLetterJobId = null;
      });
    }
  };

  private reset() {
    this.isLoading = false;
    this.jobId = null;
    this.mode = null;
    this.editorMode = 'collaboration';
    this.fileName = null;
    this.data = null;
    this.aiDraftContent = null;
    this.suggestionDetail = null;
    this.maxDetail = 1;
    this.categories = [];
    this.reportDiff = null;
    this.isDiffLoading = false;
    this.isDiffAvailable = null;
    this.originalContent = null;
    this.reviseEnabled.reset();
    this.isRevising = false;
  }
}
